import { logger } from '@adornis/base/logging.js';
import '@adornis/bh-buildify/client/nodes/institution-list/config.js';
import '@adornis/bh-buildify/client/nodes/institution-product-filter/config.js';
import '@adornis/bh-buildify/client/nodes/institution-products/config.js';
import {
  CancelledPaymentError,
  InvalidGoBackAfterBookingError,
  InvalidMissingRegistrationDataError,
  InvalidRegistrationDataError,
  InvalidStepError,
  RegistrationController,
  registrationControllerContext,
} from '@adornis/bh-registration/client/bh-registration-controller.js';
import { getColor } from '@adornis/bh-registration/client/helper/getColor.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/buchungsheld-theme/components/index.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { provide } from '@lit/context';
import { format } from 'date-fns';
import { html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { takeUntil } from 'rxjs';
import './bh-page-registration-auth.js';
import './bh-page-registration-booked.js';
import './bh-page-registration-ldap-block.js';
import './bh-page-registration-payment.js';
import './bh-page-registration-personal.js';
import './bh-page-registration-terms.js';
import './bh-page-registration-verify-mail';
import './bh-page-registration-workshop.js';
import './functional-components/RegistrationProductDetails.js';

@customElement('bh-page-registration')
export class BHPageRegistration extends BuchungsheldLitElement {
  // these are needed to customize embeds
  @property({ attribute: 'data-filter-categories', type: String }) filterCategoryIDs = new RXController<string | null>(
    this,
    null,
  );

  @state() private readonly _size = new WindowSizeController(this);

  @provide({ context: registrationControllerContext })
  @state()
  private readonly _registrationController = new RegistrationController(this);

  @state() private readonly _stepInfo = new RXController(this, this._registrationController.stepInfo$);
  @state() private readonly _institutionID = new RXController(this, this._registrationController.institutionID$);

  override connectedCallback(): void {
    super.connectedCallback();
    this.filterCategoryIDs.observable.pipe(takeUntil(this.disconnected)).subscribe(categoryIDs => {
      if (categoryIDs !== null) this._registrationController.categorieConstraints$.next(categoryIDs.split(','));
    });
  }

  override render() {
    const institutionReallyLoading = !this._institutionID.value || !this._registrationController.institution;
    if (this._stepInfo.isLoading || !this._stepInfo.value || this._institutionID.isLoading || institutionReallyLoading)
      return html`<x-loading-state></x-loading-state>`;

    // const step = this._registrationController.stepNumber;
    const stepInfo = this._stepInfo.value;

    const steps = [
      {
        id: 'workshop',
        template: html` <bh-page-registration-workshop></bh-page-registration-workshop> `,
        text: this._translation.translate('REGISTRATION', 'choose_termin'),
      },
      {
        id: 'ldapBlock',
        template: html`<bh-page-registration-ldap-block></bh-page-registration-ldap-block>`,
        text: 'LDAP-Account',
      },
      {
        id: 'auth',
        template: html` <bh-page-registration-auth></bh-page-registration-auth> `,
        text: this._translation.translate('REGISTRATION', 'auth'),
      },
      {
        id: 'loginInNewTab',
        template: html`
          <tr-flex space="md" w100 h100 center crossaxis-center>
            <tr-card>
              <tr-text> ${this._translation.translate('LOGIN_PAGE', 'loginInNewTab')} </tr-text>
              <tr-button @click=${() => this._registrationController.resetProductSelection()}>
                ${this._translation.translate('LOGIN_PAGE', 'back')}
              </tr-button>
            </tr-card>
          </tr-flex>
        `,
        text: this._translation.translate('LOGIN_PAGE', 'loginInNewTab'),
      },
      {
        id: 'personal',
        template: html` <bh-page-registration-personal></bh-page-registration-personal> `,
        text: this._translation.translate('REGISTRATION', 'personal_data'),
      },
      {
        id: 'verifyEmail',
        template: html` <bh-page-registration-verify-mail></bh-page-registration-verify-mail> `,
        text: this._translation.translate('confirm_mail', 'name'),
      },
      {
        id: 'terms',
        template: html` <bh-page-registration-terms></bh-page-registration-terms> `,
        text: this._translation.translate('termsOfUse'),
      },
      {
        id: 'payment',
        template: html` <bh-page-registration-payment></bh-page-registration-payment>`,
        text: this._translation.translate('PAYMENT', 'payment'),
      },
      {
        id: 'booked',
        template: html` <bh-page-registration-booked></bh-page-registration-booked> `,
        text: this._translation.translate('REGISTRATION', 'to_registration'),
      },
      {
        id: 'maintainanceBlock',
        template: html`
          <tr-flex space="md" w100 h100 center crossaxis-center>
            <tr-card>
              <tr-text>
                ${unsafeHTML(
                  this._registrationController.institution.maintenanceModeRender ||
                    this._translation.translate('admin_institution', 'maintenance_mode_active'),
                )}
              </tr-text>
              <tr-button href="/"> Zurück </tr-button>
            </tr-card>
          </tr-flex>
        `,
        text: this._translation.translate('settings', 'maintainance_mode'),
      },
    ].filter(sd => (stepInfo.activeSteps as string[]).includes(sd.id));

    const stepBox = (i: number, text: string | Renderable) => html`
      <tr-flex crossaxis-center class=${stepInfo.stepIndex >= i ? 'active' : 'inactive'}>
        <tr-flex center crossaxis-center padding="xs" class="step-card-text">
          <tr-text bold> ${i + 1} ${!this._size.mobile ? text : ''} </tr-text>
        </tr-flex>
      </tr-flex>
    `;

    const card = (content: TemplateResult) => {
      const cardContent = html`
        <tr-flex space="md" horizontal center wrap> ${steps.map((stepData, i) => stepBox(i, stepData.text))} </tr-flex>
        ${content}
      `;

      const inner = html` <tr-flex space="xl"> ${cardContent} </tr-flex> `;

      if (this.clientWidth < 500) return inner;
      return html`
        <x-card
          ${css({
            width: '100%',
            position: 'relative',
            overflow: 'clip',
            overflowClipMargin: '3px' /* clip teaser banner, but show box-shadow */,
          })}
        >
          ${inner}
        </x-card>
      `;
    };

    const content = (() => {
      if (this._registrationController.isLoading) return this.loadingState();
      if (this._registrationController.hasOverlappingError) return this.errorState();
      if (this._registrationController.hasOrderingAlreadyBookedProductError) return this.errorState();
      if (this._registrationController.stepInfo.ldapBlocked) return this.errorState();
      if (this._registrationController.hasError) return this.errorState();
      return steps.find(({ id }) => id === stepInfo.step)?.template ?? html` <x-loading-state></x-loading-state> `;
    })();

    return html`
      <tr-section>
        <x-flex h100 ${css({ padding: `${this.spacing.lg} 0` })}> ${card(content)} </x-flex>
      </tr-section>
    `;
  }

  errorState() {
    if (this._registrationController.hasOverlappingError)
      return html`
        <x-card ${css({ maxWidth: '30rem', margin: 'auto' })}>
          <x-flex vertical space="md" crossaxis-center>
            <x-icon ${css({ fontSize: '5em' })}>close</x-icon>
            <x-text ${css({ textAlign: 'center' })}
              >Sie haben bereits Termine gebucht die sich mit der aktuellen Auswahl überschneiden. Bitte überprüfen sie
              ihre Eingaben</x-text
            >
            <x-button @click=${() => this._registrationController.resetProductSelection()}
              >Zurück zur Anmeldung</x-button
            >
          </x-flex>
        </x-card>
      `;

    if (this._registrationController.hasOrderingAlreadyBookedProductError)
      return html`
        <x-card ${css({ maxWidth: '30rem', margin: 'auto' })}>
          <x-flex vertical space="md" crossaxis-center>
            <x-icon ${css({ fontSize: '5em' })}>close</x-icon>
            <x-text ${css({ textAlign: 'center' })}>Sie sind zu diesem Workshop bereits angemeldet</x-text>
            <x-button
              @click=${() => {
                this._registrationController.resetProductSelection();
                goTo('/');
              }}
              >Zurück zur Anmeldung</x-button
            >
          </x-flex>
        </x-card>
      `;

    if (this._registrationController.stepInfo.ldapBlocked)
      return html`
        <x-card ${css({ maxWidth: '30rem', margin: 'auto' })}>
          <x-flex vertical space="md" crossaxis-center>
            <x-icon ${css({ fontSize: '5em' })}>close</x-icon>
            <x-text ${css({ textAlign: 'center' })}>ldapBlocked</x-text>
            <x-button
              @click=${() => {
                this._registrationController.resetProductSelection();
                goTo('/');
              }}
              >Zurück zur Anmeldung</x-button
            >
          </x-flex>
        </x-card>
      `;

    if (!this._registrationController.institutionID) {
      logger.error(
        {
          institutionID: this._registrationController.institutionID,
          location: window.location.href,
          userID: this._registrationController.currentUser?._id,
        },
        'No institutionID found error',
      );
      return html`
        <x-empty-placeholder
          .text=${'How did you get here?' + ` (${format(new Date(), 'dd.MM.yyyy HH:mm:ss')})`}
          .img=${'close'}
          back-text="Zurück zur Anmeldung"
        ></x-empty-placeholder>
      `;
    }
    if (!this._registrationController.institution) {
      logger.error(
        {
          institutionID: this._registrationController.institutionID,
          location: window.location.href,
          userID: this._registrationController.currentUser?._id,
        },
        'No institution found error',
      );
      return html`
        <x-empty-placeholder
          .text=${this._translation.translate('actions', 'no_institution_error') +
          ` (${format(new Date(), 'dd.MM.yyyy HH:mm:ss')})`}
          .img=${'close'}
          back-text=${this._translation.translate('actions', 'back_to_login')}
        ></x-empty-placeholder>
      `;
    }

    if (
      this._registrationController.currentUser?.suspentions
        .map(suspension => suspension.institutionID)
        .includes(this._registrationController.institutionID)
    )
      return html`
        <x-empty-placeholder
          .text=${'Sie sind suspendiert'}
          .img=${'close'}
          back-text=${this._translation.translate('actions', 'back_to_login')}
        ></x-empty-placeholder>
      `;

    if (this._registrationController.error) {
      if (
        this._registrationController.error instanceof InvalidRegistrationDataError ||
        this._registrationController.error instanceof InvalidStepError ||
        this._registrationController.error instanceof InvalidMissingRegistrationDataError
      ) {
        logger.error(
          {
            errorMessage: this._registrationController.error.message,
            location: window.location.href,
            userID: this._registrationController.currentUser?._id,
          },
          'Error from registrationcontroller',
        );
        return html`
          <x-empty-placeholder
            .text=${this._translation.translate('actions', 'invalid_data_error') +
            ` (${format(new Date(), 'dd.MM.yyyy HH:mm:ss')})`}
            .img=${'close'}
            back-text=${this._translation.translate('actions', 'back_to_login')}
          ></x-empty-placeholder>
        `;
      }

      if (this._registrationController.error instanceof InvalidGoBackAfterBookingError) {
        logger.error(
          {
            errorMessage: this._registrationController.error.message,
            location: window.location.href,
            userID: this._registrationController.currentUser?._id,
          },
          'Error from registrationcontroller // InvalidGoBackAfterBookingError',
        );
        return html`
          <x-empty-placeholder
            .text=${'Zurück gehen nach erfolgreicher Buchung nicht erlaubt' +
            ` (${format(new Date(), 'dd.MM.yyyy HH:mm:ss')})`}
            .img=${'close'}
            back-text="Zum Profil"
          ></x-empty-placeholder>
        `;
      }

      if (this._registrationController.error instanceof CancelledPaymentError) {
        logger.error(
          {
            errorMessage: this._registrationController.error.message,
            location: window.location.href,
            userID: this._registrationController.currentUser?._id,
          },
          'Error from registrationcontroller // CancelledPaymentError',
        );
        return html`
          <x-flex space="lg">
            <x-empty-placeholder
              no-wrapper
              .img=${'close'}
              .text=${this._registrationController.host._translation.translate('cancelled_payment')}
            ></x-empty-placeholder>
            <x-flex horizontal space-between space="md">
              <tr-button href="/" mode="outline">
                ${this._registrationController.host._translation.translate('actions', 'back_to_login')}
              </tr-button>
              <tr-button @click=${() => this._registrationController._retryStripePayment()}>
                ${this._registrationController.host._translation.translate('actions', 'retry_payment')}
              </tr-button>
            </x-flex>
          </x-flex>
        `;
      }

      logger.error(
        {
          errorMessage: this._registrationController.error.message,
          location: window.location.href,
          userID: this._registrationController.currentUser?._id,
        },
        'Error from registrationcontroller // catch all',
      );
      return html`
        <x-empty-placeholder
          .text=${this._translation.translate('actions', 'invalid_link_error') +
          ` (${format(new Date(), 'dd.MM.yyyy HH:mm:ss')})`}
          .img=${'close'}
          back-text=${this._translation.translate('actions', 'back_to_login')}
          <x-empty-placeholder
          .text=${this._translation.translate('actions', 'invalid_link_error')}
          .img=${'close'}
          back-text=${this._translation.translate('actions', 'back_to_login')}
        ></x-empty-placeholder>
      `;
    }

    return html``;
  }

  loadingState() {
    if (this._registrationController.bookingProcessing)
      return html` <tr-loading-state .text=${this._translation.translate('processing_booking')}></tr-loading-state> `;
    if (this._registrationController.paymentLoading)
      return html` <tr-loading-state .text=${this._translation.translate('loading_payments')}></tr-loading-state> `;
    return html` <tr-loading-state></tr-loading-state> `;
  }

  override styles() {
    const institution = this._registrationController.institution;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!institution) return [];

    const settings = institution.settings;

    return [
      ...super.styles(),
      {
        '.step-card-text': {
          height: '25px',
          color: '#fff',
          borderRadius: this.sizes.borderRadius,
        },
        '.step-card-icon': {
          height: '25px',
          width: '25px',
          color: '#fff',
          borderRadius: this.sizes.borderRadius,
        },
        '.active .step-card-icon, .active .step-card-text': {
          background: getColor(
            settings.colorDefinitions?.registrationColorDefinitions?.stepColor,
            settings.inheritedColorDefinitions?.value?.registrationColorDefinitions?.stepColor,
            this.colors.accent,
          ),
        },
        '.inactive .step-card-icon, .inactive .step-card-text': {
          background: '#d8d8d6',
        },
      },
    ];
  }
}
